/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  Checkbox,
  Select,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
} from '@chakra-ui/react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { INota, useNotas } from '../../hooks/useNotas';
import { api } from '../../services/api';

interface IForm {
  descricao: string;
  outros: boolean;
}

interface CreateNotaModalProps {
  screen: string;
  id: number;
  dateStart: string;
  handleReFecth: () => void;
  children?: React.ReactNode;
  motivos?: string[];
}

const CreateNotaModal = ({
  screen,
  id,
  dateStart,
  handleReFecth,
  children,
  motivos: motivosProp,
}: CreateNotaModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { createNota } = useNotas();
  const [motivosApi, setMotivosApi] = useState<string[]>([]);

  const fetchMotivos = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const res = await api.get('/api/v1/anotacao-motivos/', {
          params: { tela: screen },
          signal,
        });
        const data = res.data?.data || res.data;
        const items = Array.isArray(data) ? data : [];
        setMotivosApi(items.map((m: { descricao: string }) => m.descricao));
      } catch (err: any) {
        if (err?.code !== 'ERR_CANCELED') setMotivosApi([]);
      }
    },
    [screen],
  );

  useEffect(() => {
    if (isOpen && !motivosProp) {
      const controller = new AbortController();
      fetchMotivos(controller.signal);
      return () => controller.abort();
    }
    return undefined;
  }, [isOpen, motivosProp, fetchMotivos]);

  const motivos = motivosProp || (motivosApi.length > 0 ? motivosApi : undefined);

  const schema = yup.object().shape({
    descricao: yup.string().required(motivos ? 'Selecione um motivo' : 'Descrição obrigatória'),
    outros: yup.boolean().default(true),
  });

  const { mutateAsync } = useMutation({
    mutationFn: createNota,
  });

  const formMethods = useForm<IForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      descricao: '',
      outros: true,
    },
  });

  const onSubmit = async (values: IForm) => {
    try {
      await mutateAsync({
        id,
        descricao: values.descricao,
        tela: screen,
        dt_anotacao: dateStart,
        outros: values?.outros,
      } as unknown as INota);
      handleReFecth();
      formMethods.reset();
      onClose();
      toast.success(`Nota salva com sucesso!`, {
        position: 'top-right',
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ?? 'Erro interno no sistema, tente novamente mais tarde.';
      toast.error(errorMessage, { position: 'top-right' });
    }
  };

  return (
    <>
      {children ? (
        React.cloneElement(children as React.ReactElement, { onClick: onOpen })
      ) : (
        <Button onClick={onOpen}>Criar uma nota</Button>
      )}
      <Modal isOpen={isOpen} onClose={onClose} size={motivos ? 'xl' : '6xl'}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Criar uma nota</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormProvider {...formMethods}>
              <form onSubmit={formMethods.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <Controller
                  name="descricao"
                  control={formMethods.control}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl isInvalid={!!error}>
                      <FormLabel>{motivos ? 'Motivo' : 'Descrição'}</FormLabel>
                      {motivos ? (
                        <Select placeholder="Selecione um motivo" {...field}>
                          {motivos.map(motivo => (
                            <option key={motivo} value={motivo}>
                              {motivo}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Textarea placeholder="Adicione uma descrição" {...field} />
                      )}
                      {error && <FormErrorMessage>{error.message}</FormErrorMessage>}
                    </FormControl>
                  )}
                />
                <Controller
                  name="outros"
                  control={formMethods.control}
                  render={({ field: { value, onChange, ref, ...rest } }) => (
                    <Checkbox
                      isChecked={Boolean(value)}
                      defaultChecked
                      onChange={e => onChange(e.target.checked)}
                      ref={ref}
                      {...rest}
                    >
                      Auditado
                    </Checkbox>
                  )}
                />

                <ModalFooter>
                  <Button colorScheme="blue" mr={3} onClick={onClose}>
                    Fechar
                  </Button>
                  <Button type="submit">Salvar</Button>
                </ModalFooter>
              </form>
            </FormProvider>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreateNotaModal;
